import React, { useRef, useState, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';
import { uploadData, list, getUrl, remove } from 'aws-amplify/storage';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { fetchAuthSession } from 'aws-amplify/auth';
import AdminPanel from './AdminPanel';

const AppContent: React.FC = () => {
  const webcamRef = useRef<Webcam>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [imageKeys, setImageKeys] = useState<string[]>([]);
  const [imageDates, setImageDates] = useState<string[]>([]);
  const [publicImageUrls, setPublicImageUrls] = useState<string[]>([]);
  const [publicImageKeys, setPublicImageKeys] = useState<string[]>([]);
  const [publicGalleryCount, setPublicGalleryCount] = useState<number>(0);
  const [identityId, setIdentityId] = useState<string | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [deletingPublicIndex, setDeletingPublicIndex] = useState<number | null>(null);
  const [sharingIndex, setSharingIndex] = useState<number | null>(null);
  const [currentTheme, setCurrentTheme] = useState('slate');
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);
  const [displayLogin, setDisplayLogin] = useState('');

  const { user, signOut } = useAuthenticator((context) => [context.user]);

  useEffect(() => {
    let isMounted = true;

    const fallbackLogin = user?.signInDetails?.loginId || user?.username || '';

    const resolveDisplayLogin = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        if (idToken) {
          const payloadBase64 = idToken.split('.')[1];
          if (payloadBase64) {
            const payload = JSON.parse(atob(payloadBase64)) as Record<string, unknown>;
            const email = payload.email;

            if (typeof email === 'string' && email.length > 0) {
              if (isMounted) {
                setDisplayLogin(email);
              }
              return;
            }
          }
        }
      } catch (error) {
        // Fallback to existing user identifiers when token parsing is unavailable
      }

      if (isMounted) {
        setDisplayLogin(fallbackLogin);
      }
    };

    void resolveDisplayLogin();

    return () => {
      isMounted = false;
    };
  }, [user]);

  // Theme configurations
  const themes = {
    slate: {
      name: 'Slate Gray',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
      primary: 'linear-gradient(135deg, #475569, #334155)',
      accent: 'linear-gradient(135deg, #059669, #047857)',
      danger: 'linear-gradient(135deg, #ef4444, #dc2626)'
    },
    blue: {
      name: 'Ocean Blue',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #bae6fd 100%)',
      primary: 'linear-gradient(135deg, #1e40af, #1d4ed8)',
      accent: 'linear-gradient(135deg, #0284c7, #0369a1)',
      danger: 'linear-gradient(135deg, #ef4444, #dc2626)'
    },
    purple: {
      name: 'Purple Haze',
      background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 50%, #e9d5ff 100%)',
      primary: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
      accent: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
      danger: 'linear-gradient(135deg, #ef4444, #dc2626)'
    },
    emerald: {
      name: 'Emerald Forest',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)',
      primary: 'linear-gradient(135deg, #047857, #065f46)',
      accent: 'linear-gradient(135deg, #10b981, #059669)',
      danger: 'linear-gradient(135deg, #ef4444, #dc2626)'
    }
  };

  const theme = themes[currentTheme as keyof typeof themes];

  const captureAndUpload = async () => {
    if (!webcamRef.current) return;

    const screenshot = webcamRef.current.getScreenshot();
    if (!screenshot) {
      alert('Could not capture image.');
      return;
    }

    const blob = await fetch(screenshot).then((res) => res.blob());
    const filename = `photo_${Date.now()}.jpg`;

    try {
      setUploading(true);
      await uploadData({
        key: filename,
        data: blob,
        options: {
          accessLevel: 'private',
          contentType: 'image/jpeg',
        },
      }).result;

      setUploadSuccess(filename);
      loadImageGallery(); // Refresh gallery
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. See console.');
    } finally {
      setUploading(false);
    }
  };

  const captureAndSharePublic = async () => {
    if (!webcamRef.current) return;

    const screenshot = webcamRef.current.getScreenshot();
    if (!screenshot) {
      alert('Could not capture image.');
      return;
    }

    const blob = await fetch(screenshot).then((res) => res.blob());
    const filename = `public_${Date.now()}.jpg`;

    try {
      setUploading(true);
      await uploadData({
        key: filename,
        data: blob,
        options: {
          accessLevel: 'guest',
          contentType: 'image/jpeg',
        },
      }).result;

      setUploadSuccess(`Shared: ${filename}`);
      loadPublicGallery(); // Refresh public gallery
    } catch (error) {
      console.error('Public upload error:', error);
      alert('Public upload failed. See console.');
    } finally {
      setUploading(false);
    }
  };

  const loadImageGallery = useCallback(async () => {
    if (!identityId) {
      console.log('No identityId available, skipping gallery load');
      return;
    }

    try {
      console.log('Listing files for identityId:', identityId);
      
      // Ensure we have a valid session before attempting to list files
      const session = await fetchAuthSession();
      if (!session.credentials) {
        console.log('No valid credentials, user might not be authenticated');
        return;
      }
      
      const result = await list({
        options: {
          accessLevel: 'private',
          listAll: true, // This ensures you get all files, not just the first page
        },
      });
      console.log('List result:', result.items);
      
      // Sort items by lastModified date (newest first)
      const sortedItems = result.items
        .filter(item => item.key && item.lastModified)
        .sort((a, b) => new Date(b.lastModified!).getTime() - new Date(a.lastModified!).getTime());
      
      // Store keys, URLs, and formatted dates
      const keys = sortedItems.map(item => item.key!);
      const dates = sortedItems.map(item => {
        const date = new Date(item.lastModified!);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      });
      
      const signedUrls = await Promise.all(
        sortedItems.map((item) =>
          getUrl({
            key: item.key!,
            options: { accessLevel: 'private' },
          }).then((urlResult) => urlResult.url.toString())
        )
      );

      setImageKeys(keys);
      setImageDates(dates);
      setImageUrls(signedUrls);
    } catch (error) {
      console.error('Failed to load image gallery:', error);
    }
  }, [identityId]);

  const loadPublicGallery = useCallback(async () => {
    try {
      const result = await list({
        options: {
          accessLevel: 'guest',
          listAll: true,
        },
      });
      
      // Store both URLs and keys for deletion
      const keys = result.items.map(item => item.key!);
      const publicUrls = await Promise.all(
        result.items.map((item) =>
          getUrl({
            key: item.key!,
            options: { accessLevel: 'guest' },
          }).then((urlResult) => urlResult.url.toString())
        )
      );
      
      setPublicImageKeys(keys);
      setPublicImageUrls(publicUrls);
      setPublicGalleryCount(keys.length);
    } catch (error) {
      console.error('Failed to load public gallery:', error);
    }
  }, []);

  const shareToPublic = async (index: number) => {
    try {
      setSharingIndex(index);
      const keyToShare = imageKeys[index];
      
      // Create a new public key
      const publicKey = `shared_${Date.now()}_${keyToShare.replace(/^photo_/, '')}`;
      
      // Use getUrl to get the private image URL with download capability
      const privateUrlResult = await getUrl({
        key: keyToShare,
        options: { 
          accessLevel: 'private',
          validateObjectExistence: false
        }
      });
      
      // Fetch the image data using the Amplify URL (this should work without CORS issues)
      const response = await fetch(privateUrlResult.url.toString());
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Upload to public gallery
      await uploadData({
        key: publicKey,
        data: blob,
        options: {
          accessLevel: 'guest',
          contentType: 'image/jpeg',
        },
      }).result;
      
      // Refresh public gallery
      loadPublicGallery();
      
      console.log('Successfully shared image to public gallery');
      setUploadSuccess(`Shared to community: ${publicKey}`);
    } catch (error) {
      console.error('Failed to share image:', error);
      // More specific error handling
      if (error instanceof Error && error.message.includes('CORS')) {
        alert('Unable to share this image due to browser security restrictions. Try using "Share Public" when taking new photos.');
      } else {
        alert('Failed to share image. Please try again or contact support.');
      }
    } finally {
      setSharingIndex(null);
    }
  };

  const deletePublicImage = async (index: number) => {
    try {
      setDeletingPublicIndex(index);
      const keyToDelete = publicImageKeys[index];
      
      await remove({
        key: keyToDelete,
        options: {
          accessLevel: 'guest'
        }
      });
      
      // Remove from local state
      const newPublicUrls = publicImageUrls.filter((_, i) => i !== index);
      const newPublicKeys = publicImageKeys.filter((_, i) => i !== index);
      
      setPublicImageUrls(newPublicUrls);
      setPublicImageKeys(newPublicKeys);
      
      console.log('Successfully deleted public image:', keyToDelete);
    } catch (error) {
      console.error('Failed to delete public image:', error);
      alert('Failed to delete public image. You can only delete images you shared.');
    } finally {
      setDeletingPublicIndex(null);
    }
  };

  const deleteImage = async (index: number) => {
    try {
      setDeletingIndex(index);
      const keyToDelete = imageKeys[index];
      
      await remove({
        key: keyToDelete,
        options: {
          accessLevel: 'private'
        }
      });
      
      // Remove from local state
      const newImageUrls = imageUrls.filter((_, i) => i !== index);
      const newImageKeys = imageKeys.filter((_, i) => i !== index);
      const newImageDates = imageDates.filter((_, i) => i !== index);
      
      setImageUrls(newImageUrls);
      setImageKeys(newImageKeys);
      setImageDates(newImageDates);
      
      console.log('Successfully deleted image:', keyToDelete);
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert('Failed to delete image. See console for details.');
    } finally {
      setDeletingIndex(null);
    }
  };

  useEffect(() => {
    const fetchIdentity = async () => {
      try {
        // Wait a bit to ensure authentication is complete
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const session = await fetchAuthSession();
        console.log('Fetched session:', session);
        console.log('Fetched identityId:', session.identityId);
        console.log('Has credentials:', !!session.credentials);
        
        if (session.identityId && session.credentials) {
          setIdentityId(session.identityId);
        } else {
          console.log('Session missing identityId or credentials');
        }
      } catch (err) {
        console.error('Session fetch error:', err);
      }
    };

    if (user) {
      fetchIdentity();
    }
  }, [user]);

  useEffect(() => {
    if (identityId) {
      loadImageGallery();
    }
    // Load public gallery for all users
    loadPublicGallery();
  }, [identityId, loadImageGallery, loadPublicGallery]);

  // Auto-refresh public gallery every 8 seconds to sync changes across users
  useEffect(() => {
    const pollPublicGallery = async () => {
      try {
        const result = await list({
          options: {
            accessLevel: 'guest',
            listAll: true,
          },
        });
        
        const currentCount = result.items.length;
        
        // Only reload if the count has changed (items added or removed)
        if (currentCount !== publicGalleryCount) {
          console.log(`🎬 Community Film Strip updated: ${publicGalleryCount} → ${currentCount} images`);
          loadPublicGallery();
        }
      } catch (error) {
        // Silently fail to avoid console spam during network issues
      }
    };

    // Start polling after initial load
    const pollInterval = setInterval(pollPublicGallery, 8000); // Poll every 8 seconds

    return () => clearInterval(pollInterval);
  }, [publicGalleryCount, loadPublicGallery]); // Re-run when count changes

  // Show admin panel if requested
  if (showAdmin) {
    return <AdminPanel theme={theme} onBack={() => setShowAdmin(false)} />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.background,
      padding: '1rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '20px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        padding: '2rem',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            margin: '0 0 1rem 0',
            background: theme.primary,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: '700'
          }}>
            📸 Virtual Photo Booth
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: '#666',
            margin: '0 0 1rem 0'
          }}>
            Welcome, <strong>{displayLogin || user?.signInDetails?.loginId || user?.username}</strong>
          </p>
          
          {/* Theme Selector */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
            flexWrap: 'wrap'
          }}>
            {Object.entries(themes).map(([key, themeConfig]) => (
              <button
                key={key}
                onClick={() => setCurrentTheme(key)}
                style={{
                  background: currentTheme === key ? themeConfig.primary : themeConfig.background,
                  color: currentTheme === key ? 'white' : '#666',
                  border: currentTheme === key ? 'none' : '2px solid #e2e8f0',
                  padding: '0.5rem 1rem',
                  borderRadius: '25px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '80px'
                }}
                title={`Switch to ${themeConfig.name}`}
              >
                {themeConfig.name}
              </button>
            ))}
          </div>
          
          <button 
            onClick={signOut}
            style={{
              background: theme.danger,
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '50px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(220, 38, 38, 0.25)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.35)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(220, 38, 38, 0.25)';
            }}
          >
            Sign Out
          </button>
          
          <button 
            onClick={() => setShowAdmin(true)}
            style={{
              background: theme.primary,
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '50px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(71, 85, 105, 0.25)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(71, 85, 105, 0.35)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(71, 85, 105, 0.25)';
            }}
          >
            🛡️ Admin
          </button>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.5rem',
          marginBottom: '3rem'
        }}>
          {/* Camera Toggle Button */}
          <button 
            onClick={() => setIsCameraOn(!isCameraOn)}
            style={{
              background: isCameraOn ? theme.danger : theme.accent,
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '50px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: isCameraOn 
                ? '0 4px 15px rgba(220, 38, 38, 0.25)'
                : '0 4px 15px rgba(5, 150, 105, 0.25)',
              minWidth: '150px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = isCameraOn
                ? '0 6px 20px rgba(220, 38, 38, 0.35)'
                : '0 6px 20px rgba(5, 150, 105, 0.35)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = isCameraOn
                ? '0 4px 15px rgba(220, 38, 38, 0.25)'
                : '0 4px 15px rgba(5, 150, 105, 0.25)';
            }}
          >
            {isCameraOn ? '📴 Turn Camera Off' : '📹 Turn Camera On'}
          </button>

          {/* Camera Container */}
          <div style={{
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            border: '4px solid white',
            background: isCameraOn ? 'transparent' : '#f8f9fa',
            position: 'relative'
          }}>
            {isCameraOn ? (
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: 'auto',
                  display: 'block'
                }}
                videoConstraints={{
                  width: { ideal: 1280 },
                  height: { ideal: 720 },
                  facingMode: 'user'
                }}
              />
            ) : (
              <div style={{
                width: '400px',
                height: '300px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                color: '#6c757d',
                fontSize: '1.2rem',
                fontWeight: '600'
              }}>
                <div style={{
                  fontSize: '4rem',
                  marginBottom: '1rem',
                  opacity: 0.5
                }}>
                  📷
                </div>
                <p style={{ margin: 0, textAlign: 'center' }}>
                  Camera is off
                </p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.7 }}>
                  Turn on camera to take photos
                </p>
              </div>
            )}
          </div>
          
          <div style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <button 
              onClick={captureAndUpload} 
              disabled={uploading || !isCameraOn}
              style={{
                background: uploading || !isCameraOn
                  ? 'linear-gradient(135deg, #94a3b8, #64748b)' 
                  : theme.accent,
                color: 'white',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '50px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: uploading || !isCameraOn ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: uploading || !isCameraOn
                  ? '0 4px 15px rgba(148, 163, 184, 0.25)' 
                  : '0 4px 15px rgba(5, 150, 105, 0.3)',
                minWidth: '200px',
                opacity: !isCameraOn ? 0.6 : 1
              }}
              onMouseOver={(e) => {
                if (!uploading && isCameraOn) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(5, 150, 105, 0.4)';
                }
              }}
              onMouseOut={(e) => {
                if (!uploading && isCameraOn) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(5, 150, 105, 0.3)';
                }
              }}
              title={!isCameraOn ? 'Turn on camera to capture photos' : ''}
            >
              {uploading ? '📤 Uploading...' : '📸 Capture Private'}
            </button>
            
            <button 
              onClick={captureAndSharePublic} 
              disabled={uploading || !isCameraOn}
              style={{
                background: uploading || !isCameraOn
                  ? 'linear-gradient(135deg, #94a3b8, #64748b)' 
                  : theme.primary,
                color: 'white',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '50px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: uploading || !isCameraOn ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: uploading || !isCameraOn
                  ? '0 4px 15px rgba(148, 163, 184, 0.25)' 
                  : '0 4px 15px rgba(71, 85, 105, 0.3)',
                minWidth: '200px',
                opacity: !isCameraOn ? 0.6 : 1
              }}
              onMouseOver={(e) => {
                if (!uploading && isCameraOn) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(71, 85, 105, 0.4)';
                }
              }}
              onMouseOut={(e) => {
                if (!uploading && isCameraOn) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(71, 85, 105, 0.3)';
                }
              }}
              title={!isCameraOn ? 'Turn on camera to share photos publicly' : ''}
            >
              {uploading ? '🌍 Sharing...' : '🌍 Share Public'}
            </button>
          </div>
        </div>

        {uploadSuccess && (
          <div style={{
            background: 'linear-gradient(135deg, #d4edda, #c3e6cb)',
            border: '1px solid #c3e6cb',
            borderRadius: '15px',
            padding: '1rem',
            marginBottom: '2rem',
            textAlign: 'center',
            color: '#155724'
          }}>
            <p style={{ margin: 0, fontSize: '1rem' }}>
              ✅ Successfully uploaded: <code style={{
                background: 'rgba(21, 87, 36, 0.1)',
                padding: '0.25rem 0.5rem',
                borderRadius: '8px',
                fontFamily: 'Monaco, Consolas, monospace'
              }}>{uploadSuccess}</code>
            </p>
          </div>
        )}

        {/* Public Gallery Film Strip */}
        <div style={{
          borderTop: '2px solid #eee',
          paddingTop: '2rem',
          marginTop: '2rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)',
            textAlign: 'center',
            margin: '0 0 1.5rem 0',
            color: '#333',
            fontWeight: '600'
          }}>
            � Community Film Strip
          </h2>
          
          {/* Film Strip Container */}
          <div style={{
            background: 'linear-gradient(135deg, #1f1f1f 0%, #2d2d2d 50%, #1a1a1a 100%)',
            padding: '20px 0',
            borderRadius: '10px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Film Strip Sprocket Holes - Top */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '20px',
              background: 'repeating-linear-gradient(90deg, transparent 0px, transparent 20px, #000 20px, #000 30px)',
              zIndex: 1
            }} />
            
            {/* Film Strip Sprocket Holes - Bottom */}
            <div style={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              right: '0',
              height: '20px',
              background: 'repeating-linear-gradient(90deg, transparent 0px, transparent 20px, #000 20px, #000 30px)',
              zIndex: 1
            }} />
            
            {/* Film Strip Perforations - Left Side */}
            <div style={{
              position: 'absolute',
              top: '20px',
              bottom: '20px',
              left: '5px',
              width: '10px',
              background: 'repeating-linear-gradient(0deg, #000 0px, #000 8px, transparent 8px, transparent 16px)',
              zIndex: 1
            }} />
            
            {/* Film Strip Perforations - Right Side */}
            <div style={{
              position: 'absolute',
              top: '20px',
              bottom: '20px',
              right: '5px',
              width: '10px',
              background: 'repeating-linear-gradient(0deg, #000 0px, #000 8px, transparent 8px, transparent 16px)',
              zIndex: 1
            }} />
            
            {/* Film Strip Images Container */}
            <div style={{
              display: 'flex',
              overflowX: 'auto',
              gap: '8px',
              padding: '20px 25px',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              position: 'relative',
              zIndex: 2
            }}>
              {publicImageUrls.length > 0 ? (
                publicImageUrls.map((url, index) => (
                  <div
                    key={index}
                    style={{
                      minWidth: '160px',
                      height: '120px',
                      border: '2px solid #000',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                      transition: 'all 0.3s ease',
                      background: '#fff',
                      scrollSnapAlign: 'start',
                      position: 'relative',
                      flexShrink: 0
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px) scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.6)';
                      e.currentTarget.style.border = '2px solid #333';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.5)';
                      e.currentTarget.style.border = '2px solid #000';
                    }}
                  >
                    <img
                      src={url}
                      alt={`Film frame ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        filter: 'sepia(10%) contrast(1.1)'
                      }}
                    />
                    <button
                      onClick={() => deletePublicImage(index)}
                      disabled={deletingPublicIndex === index}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        background: deletingPublicIndex === index 
                          ? 'linear-gradient(135deg, #94a3b8, #64748b)' 
                          : 'rgba(239, 68, 68, 0.9)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        cursor: deletingPublicIndex === index ? 'not-allowed' : 'pointer',
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
                        transition: 'all 0.3s ease',
                        opacity: 0.8,
                        backdropFilter: 'blur(4px)'
                      }}
                      onMouseOver={(e) => {
                        if (deletingPublicIndex !== index) {
                          e.currentTarget.style.opacity = '1';
                          e.currentTarget.style.transform = 'scale(1.15)';
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 1)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (deletingPublicIndex !== index) {
                          e.currentTarget.style.opacity = '0.8';
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)';
                        }
                      }}
                      title={deletingPublicIndex === index ? 'Deleting...' : 'Delete from community (only your own images)'}
                    >
                      {deletingPublicIndex === index ? '⏳' : '🗑️'}
                    </button>
                  </div>
                ))
              ) : (
                <div style={{
                  width: '100%',
                  textAlign: 'center',
                  padding: '2rem',
                  color: '#ccc',
                  fontSize: '1rem'
                }}>
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '0.5rem',
                    opacity: 0.7
                  }}>
                    �️
                  </div>
                  <p style={{ margin: 0 }}>
                    No film frames yet. Be the first to add to the reel!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{
          borderTop: '2px solid #eee',
          paddingTop: '2rem',
          marginTop: '2rem'
        }}>
          <h2 style={{
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            textAlign: 'center',
            margin: '0 0 2rem 0',
            color: '#333',
            fontWeight: '600'
          }}>
            🖼️ Your Photo Gallery
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '1.5rem',
            justifyItems: 'center'
          }}>
            {imageUrls.length > 0 ? (
              imageUrls.map((url, index) => (
                <div
                  key={index}
                  style={{
                    borderRadius: '15px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.3s ease',
                    background: 'white',
                    padding: '8px',
                    position: 'relative'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                  }}
                  title={`Captured: ${imageDates[index] || 'Unknown date'}`}
                >
                  <img
                    src={url}
                    alt={`Snapshot ${index + 1}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '10px',
                      display: 'block'
                    }}
                  />
                  <button
                    onClick={() => shareToPublic(index)}
                    disabled={sharingIndex === index}
                    style={{
                      position: 'absolute',
                      top: '15px',
                      left: '15px',
                      background: sharingIndex === index 
                        ? 'linear-gradient(135deg, #94a3b8, #64748b)' 
                        : theme.accent,
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '35px',
                      height: '35px',
                      cursor: sharingIndex === index ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                      transition: 'all 0.3s ease',
                      opacity: 0.9
                    }}
                    onMouseOver={(e) => {
                      if (sharingIndex !== index) {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.4)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (sharingIndex !== index) {
                        e.currentTarget.style.opacity = '0.9';
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                      }
                    }}
                    title={sharingIndex === index ? 'Sharing...' : 'Share to community'}
                  >
                    {sharingIndex === index ? '⏳' : '🌍'}
                  </button>
                  <button
                    onClick={() => deleteImage(index)}
                    disabled={deletingIndex === index}
                    style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      background: deletingIndex === index 
                        ? 'linear-gradient(135deg, #94a3b8, #64748b)' 
                        : theme.danger,
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '35px',
                      height: '35px',
                      cursor: deletingIndex === index ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                      transition: 'all 0.3s ease',
                      opacity: 0.9
                    }}
                    onMouseOver={(e) => {
                      if (deletingIndex !== index) {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (deletingIndex !== index) {
                        e.currentTarget.style.opacity = '0.9';
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                      }
                    }}
                    title={deletingIndex === index ? 'Deleting...' : 'Delete image'}
                  >
                    {deletingIndex === index ? '⏳' : '🗑️'}
                  </button>
                </div>
              ))
            ) : (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '3rem',
                color: '#666',
                fontSize: '1.1rem'
              }}>
                <div style={{
                  fontSize: '4rem',
                  marginBottom: '1rem',
                  opacity: 0.5
                }}>
                  📷
                </div>
                <p style={{ margin: 0 }}>
                  No photos captured yet. Start taking some pictures!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const App = () => (
  <Authenticator
    socialProviders={['google', 'facebook']}
    loginMechanisms={['email']}
  >
    <AppContent />
  </Authenticator>
);

export default App;
